import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ParticularService from '../../services/ParticularService';
import particularesApiService from '../../services/ParticularesApiService';
import UsuarioService from '../../services/UsuarioService';
import { isAdminColegio } from '../../utils/adminMenu';
import { isParticularChatGPTAllowed } from '../../utils/particularChatGPTAccess';
import LoadingSpinner from './LoadingSpinner';
import SubscriptionBlockedModal from './SubscriptionBlockedModal';

const isParticularScope = (value) => {
  const normalized = (value || '').toString().trim().toLowerCase();
  return normalized === 'particular' || normalized === 'particulares';
};

let syncedParticularTokenId = '';

const ParticularChatGPTRouteGuard = ({ children }) => {
  const { user, updateToken, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [isBlocked, setIsBlocked] = useState(false);
  const syncedParticularRef = useRef('');
  const updateTokenRef = useRef(updateToken);

  const activeParticular = useMemo(() => ParticularService.getActiveParticular(), []);
  const activeParticularId = useMemo(() => ParticularService.getActiveParticularId(), []);
  const roleName = (user?.roleName || user?.nombreRol || user?.role || '').toLowerCase();
  const requestedScope = location.state?.scope || new URLSearchParams(location.search).get('scope');
  const isGeneralAdmin = roleName.includes('admin') && !isAdminColegio(user);
  const isParticularRole = roleName.includes('particular');
  const isExplicitParticularScope = isParticularScope(requestedScope);
  const isParticularContext = isExplicitParticularScope ||
    isParticularRole ||
    (isGeneralAdmin && Boolean(activeParticular || activeParticularId));

  useEffect(() => {
    updateTokenRef.current = updateToken;
  }, [updateToken]);

  useEffect(() => {
    if (authLoading) return undefined;

    let cancelled = false;

    const verifyAccess = async () => {
      if (!isParticularContext) {
        syncedParticularRef.current = '';
        syncedParticularTokenId = '';
        setIsBlocked(false);
        setIsChecking(false);
        return;
      }

      setIsChecking(true);

      try {
        const activeParticularKey = activeParticularId ? String(activeParticularId) : '';
        if (
          activeParticularKey &&
          isGeneralAdmin &&
          syncedParticularRef.current !== activeParticularKey &&
          syncedParticularTokenId !== activeParticularKey
        ) {
          const selectResponse = await UsuarioService.selectParticular(activeParticularId);
          const token = selectResponse?.token || selectResponse?.Token;
          syncedParticularRef.current = activeParticularKey;
          syncedParticularTokenId = activeParticularKey;
          if (token) {
            updateTokenRef.current(token);
          }
        }

        const contextResponse = await particularesApiService.getContexto();
        if (cancelled) return;

        const particular = contextResponse?.particular || contextResponse?.Particular || activeParticular || null;
        if (particular) {
          ParticularService.setActiveParticular(particular);
        }

        setIsBlocked(!isParticularChatGPTAllowed(particular));
      } catch (_) {
        if (!cancelled) {
          setIsBlocked(true);
        }
      } finally {
        if (!cancelled) {
          setIsChecking(false);
        }
      }
    };

    verifyAccess();

    return () => {
      cancelled = true;
    };
  }, [activeParticular, activeParticularId, authLoading, isGeneralAdmin, isParticularContext]);

  const handleCloseBlocked = () => {
    navigate('/particular', { replace: true, state: { tab: 'resumen' } });
  };

  if (isChecking) {
    return (
      <div className="dashboard-container">
        <div className="loading-container">
          <LoadingSpinner message="Verificando suscripción..." />
        </div>
      </div>
    );
  }

  if (isBlocked) {
    return (
      <div className="dashboard-container">
        <SubscriptionBlockedModal open onClose={handleCloseBlocked} />
      </div>
    );
  }

  return children;
};

export default ParticularChatGPTRouteGuard;
